'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FeedbackData {
  rating: number;
  category: string;
  name: string;
  email: string;
  message: string;
}

const FeedbackContactSection = () => {
  const [formData, setFormData] = useState<FeedbackData>({
    rating: 0,
    category: '',
    name: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [errors, setErrors] = useState<Partial<Record<keyof FeedbackData, string>>>({});

  const categories = [
    { value: 'suggestion', label: 'Sugerencia', icon: 'LightBulbIcon' },
    { value: 'improvement', label: 'Mejora', icon: 'WrenchScrewdriverIcon' },
    { value: 'comment', label: 'Comentario', icon: 'ChatBubbleLeftIcon' },
    { value: 'feedback', label: 'Feedback General', icon: 'ChatBubbleBottomCenterTextIcon' },
    { value: 'other', label: 'Otro', icon: 'EllipsisHorizontalCircleIcon' },
  ];

  const handleRatingClick = (value: number) => {
    setFormData(prev => ({ ...prev, rating: value }));
    setErrors(prev => ({ ...prev, rating: '' }));
  };

  const handleInputChange = (field: keyof FeedbackData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FeedbackData, string>> = {};

    if (formData.rating === 0) {
      newErrors.rating = 'Por favor califica tu experiencia';
    }
    if (!formData.category) {
      newErrors.category = 'Por favor selecciona una categoría';
    }
    if (!formData.name.trim()) {
      newErrors.name = 'Por favor ingresa tu nombre';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Por favor ingresa tu correo electrónico';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Por favor ingresa un correo electrónico válido';
    }
    if (!formData.message.trim()) {
      newErrors.message = 'Por favor escribe tu mensaje';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        const message = data?.error?.message || 'No pudimos enviar tu feedback. Inténtalo de nuevo.';
        throw new Error(message);
      }

      setSubmitSuccess(true);
      setFormData({
        rating: 0,
        category: '',
        name: '',
        email: '',
        message: '',
      });

      // Reset success message after 5 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No pudimos enviar tu feedback. Inténtalo de nuevo.';
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 lg:py-32 relative overflow-hidden text-white" style={{ backgroundImage: 'linear-gradient(135deg, #06213f 0%, #0f3f87 34%, #2c78fb 68%, #3ee0be 100%)' }}>
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-96 h-96 bg-[#2c78fb]/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#3ee0be]/20 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 px-5 py-2.5 bg-white/14 backdrop-blur-md rounded-full mb-8 border border-white/20 shadow-lg">
            <Icon name="ChatBubbleLeftRightIcon" size={20} className="text-[#9ff9de]" />
            <span className="text-sm text-white font-semibold">Tu Opinión Importa</span>
          </div>

          <h2 className="text-4xl lg:text-5xl font-black text-white mb-6 leading-tight">
            Queremos{' '}
            <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(135deg, #ffffff 0%, #b8fff0 45%, #7dd3fc 100%)' }}>
              Escucharte
            </span>
          </h2>

          <p className="text-xl text-white/95 leading-relaxed max-w-3xl mx-auto">
            Tu feedback es fundamental para mejorar LynMet. Comparte tus sugerencias, comentarios o simplemente cuéntanos qué te parece la plataforma.
          </p>
        </div>

        {submitSuccess && (
          <div className="mb-8 bg-green-500/20 backdrop-blur-md border border-green-300/50 rounded-xl p-6 text-center">
            <Icon name="CheckCircleIcon" size={48} className="text-green-300 mx-auto mb-3" />
            <h3 className="text-xl font-bold text-white mb-2">¡Gracias por tu Feedback!</h3>
            <p className="text-white/90">Hemos recibido tu mensaje y lo revisaremos pronto.</p>
          </div>
        )}
        {submitError && (
          <div className="mb-8 bg-red-500/20 backdrop-blur-md border border-red-300/50 rounded-xl p-6 text-center">
            <Icon name="ExclamationCircleIcon" size={48} className="text-red-200 mx-auto mb-3" />
            <h3 className="text-xl font-bold text-white mb-2">No pudimos enviar tu feedback</h3>
            <p className="text-white/90">{submitError}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white/12 backdrop-blur-md rounded-2xl p-8 lg:p-10 border border-white/20 shadow-2xl">
          {/* Star Rating */}
          <div className="mb-8">
            <label className="block text-white font-semibold mb-4 text-lg">
              ¿Qué te parece LynMet? <span className="text-yellow-300">*</span>
            </label>
            <div className="flex items-center justify-center space-x-3">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => handleRatingClick(star)}
                  className="transition-all hover:scale-110 focus:outline-none focus:ring-2 focus:ring-yellow-300 rounded"
                  aria-label={`Calificar ${star} de 5 estrellas`}
                >
                  <Icon
                    name="StarIcon"
                    variant={star <= formData.rating ? 'solid' : 'outline'}
                    size={40}
                    className={star <= formData.rating ? 'text-yellow-300' : 'text-white/40'}
                  />
                </button>
              ))}
            </div>
            {formData.rating > 0 && (
              <p className="text-center text-white/90 mt-3 text-lg font-medium">
                {formData.rating === 5 && '¡Excelente! 🎉'}
                {formData.rating === 4 && '¡Muy bien! 👍'}
                {formData.rating === 3 && 'Bien 👌'}
                {formData.rating === 2 && 'Puede mejorar 🤔'}
                {formData.rating === 1 && 'Necesitamos mejorar 😔'}
              </p>
            )}
            {errors.rating && (
              <p className="text-center text-yellow-300 mt-2 text-sm flex items-center justify-center space-x-1">
                <Icon name="ExclamationCircleIcon" size={16} />
                <span>{errors.rating}</span>
              </p>
            )}
          </div>

          {/* Category Selection */}
          <div className="mb-6">
            <label className="block text-white font-semibold mb-3">
              Categoría <span className="text-yellow-300">*</span>
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {categories.map((cat) => (
                <button
                  key={cat.value}
                  type="button"
                  onClick={() => handleInputChange('category', cat.value)}
                  className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                    formData.category === cat.value
                      ? 'bg-white text-[var(--primary-strong)] border-white' :'bg-white/10 text-white border-white/25 hover:bg-white/18'
                  }`}
                >
                  <Icon name={cat.icon} size={24} className="mx-auto mb-2" />
                  <span className="text-sm font-medium">{cat.label}</span>
                </button>
              ))}
            </div>
            {errors.category && (
              <p className="text-yellow-300 mt-2 text-sm flex items-center space-x-1">
                <Icon name="ExclamationCircleIcon" size={16} />
                <span>{errors.category}</span>
              </p>
            )}
          </div>

          {/* Name */}
          <div className="mb-6">
            <label htmlFor="name" className="block text-white font-semibold mb-2">
              Nombre <span className="text-yellow-300">*</span>
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-white/20 backdrop-blur-sm border border-white/30 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50"
              placeholder="Tu nombre"
            />
            {errors.name && (
              <p className="text-yellow-300 mt-2 text-sm flex items-center space-x-1">
                <Icon name="ExclamationCircleIcon" size={16} />
                <span>{errors.name}</span>
              </p>
            )}
          </div>

          {/* Email */}
          <div className="mb-6">
            <label htmlFor="email" className="block text-white font-semibold mb-2">
              Correo Electrónico <span className="text-yellow-300">*</span>
            </label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-white/20 backdrop-blur-sm border border-white/30 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50"
              placeholder="tu@email.com"
            />
            {errors.email && (
              <p className="text-yellow-300 mt-2 text-sm flex items-center space-x-1">
                <Icon name="ExclamationCircleIcon" size={16} />
                <span>{errors.email}</span>
              </p>
            )}
          </div>

          {/* Message */}
          <div className="mb-8">
            <label htmlFor="message" className="block text-white font-semibold mb-2">
              Tu Mensaje <span className="text-yellow-300">*</span>
            </label>
            <textarea
              id="message"
              value={formData.message}
              onChange={(e) => handleInputChange('message', e.target.value)}
              rows={5}
              className="w-full px-4 py-3 rounded-xl bg-white/20 backdrop-blur-sm border border-white/30 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 resize-none"
              placeholder="Comparte tus sugerencias, comentarios o feedback..."
            />
            {errors.message && (
              <p className="text-yellow-300 mt-2 text-sm flex items-center space-x-1">
                <Icon name="ExclamationCircleIcon" size={16} />
                <span>{errors.message}</span>
              </p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="btn-primary-app w-full py-4 text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {isSubmitting ? (
              <>
                <Icon name="ArrowPathIcon" size={24} className="animate-spin" />
                <span>Enviando...</span>
              </>
            ) : (
              <>
                <Icon name="PaperAirplaneIcon" size={24} />
                <span>Enviar Feedback</span>
              </>
            )}
          </button>
        </form>

        {/* Contact Info */}
        <div className="mt-12 text-center">
          <p className="text-white/90 mb-4">¿Prefieres contactarnos directamente?</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="mailto:contacto@lynmet.com"
              className="inline-flex items-center space-x-2 px-6 py-3 bg-white/20 backdrop-blur-md text-white rounded-xl border border-white/30 hover:bg-white/30 transition-all duration-300"
            >
              <Icon name="EnvelopeIcon" size={20} />
              <span>contacto@lynmet.com</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeedbackContactSection;
